import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { TbChevronDown, TbChevronUp } from "react-icons/tb";
import { CurrencyIcon } from "../../../../Components/Atoms/CurrencyIcon";
import { useFxQuotes } from "../../../../Hooks/useFxQuotes";
import { CONTA_INTERNACIONAL_HOME_PATH } from "../constants";

const QuoteItemSkeleton = () => (
  <div className="quote-carousel-item animate-pulse">
    <div className="h-8 w-8 shrink-0 rounded-full bg-gray-200" />
    <div className="min-w-0 space-y-1">
      <div className="h-3 w-16 rounded bg-gray-200" />
      <div className="h-4 w-24 rounded bg-gray-200" />
    </div>
  </div>
);

const QuoteItem = ({ quote }) => (
  <div className="quote-carousel-item">
    <CurrencyIcon asset={quote.asset} iconUrl={quote.iconUrl} preserveColor />
    <div className="min-w-0">
      <p className="text-[11px] text-gray-500">{quote.pair}</p>
      <div className="flex items-center gap-1">
        <span className="text-sm font-bold text-brand-dark">{quote.price}</span>
        {quote.change != null && (
          <span
            className={`flex items-center text-[11px] font-medium ${
              quote.positive ? "text-emerald-600" : "text-red-500"
            }`}
          >
            {quote.positive ? (
              <TbChevronUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
            ) : (
              <TbChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
            )}
            {quote.change}
          </span>
        )}
      </div>
    </div>
  </div>
);

export const FxQuotesCarousel = ({
  showViewAll = true,
  viewAllLink = CONTA_INTERNACIONAL_HOME_PATH,
  compact = false,
  embedded = false,
}) => {
  const trackRef = useRef(null);
  const { quotes, loading, refreshing } = useFxQuotes();
  const isInitialLoading = loading && quotes.length === 0;
  const isSilentRefresh = refreshing || (loading && quotes.length > 0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || isInitialLoading || !quotes.length) return;

    const interval = setInterval(() => {
      const firstItem = track.querySelector(".quote-carousel-item");
      const step = firstItem ? firstItem.offsetWidth + 24 : 180;

      if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 8) {
        track.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        track.scrollBy({ left: step, behavior: "smooth" });
      }
    }, 4500);

    return () => clearInterval(interval);
  }, [isInitialLoading, quotes.length]);

  return (
    <section
      className={`quotes-carousel-bar ${
        embedded ? "" : compact ? "mb-4" : "mb-5"
      }`}
    >
      <h3 className="shrink-0 text-sm font-bold text-brand-dark">Cotações</h3>

      {isInitialLoading ? (
        <div className="quotes-carousel-track">
          {Array.from({ length: 4 }).map((_, index) => (
            <QuoteItemSkeleton key={`quote-skeleton-${index}`} />
          ))}
        </div>
      ) : (
        <div
          ref={trackRef}
          className={`quotes-carousel-track${isSilentRefresh ? " quotes-carousel-track--refreshing" : ""}`}
        >
          {quotes.map((quote) => (
            <QuoteItem key={quote.id} quote={quote} />
          ))}
        </div>
      )}

      {showViewAll && (
        <Link to={viewAllLink} className="btn-outline !shrink-0 !rounded-full !px-4">
          Ver todas
        </Link>
      )}
    </section>
  );
};
